'use client';

import { ReactElement, useRef, useState } from 'react';
import classNames from 'classnames';
import { Icon } from '@nx-frontend/shared/ui/icon';
import { IconsMap } from '@nx-frontend/shared/ui/icon/icon';
import { TippyTooltip } from '@nx-frontend/shared/ui/tooltip';
import { useHover } from '@nx-frontend/shared/hooks';
import { UiTooltipPosition } from '@nx-frontend/shared/types';
import {
  ContextMenuItemData,
  ContextMenuItemType,
  MenuItemDefault,
  UiContextMenuItemProps,
} from '@nx-frontend/shared/types';
import { getContextSubMenuPosition } from './calculate-context-menu-position';
import { ContextMenuPopperWrapper } from './context-menu-popper-wrapper';
import { ContextMenuSub } from './context-menu-sub';

export const ContextMenuItem: React.FC<UiContextMenuItemProps> = ({
  item,
  closeContextMenu,
  subX,
  subY,
  theme,
  classes,
  usePopperSubmenu,
}) => {
  const elementRef = useRef<HTMLLIElement | null>(null);
  const [popperReferenceEl, setPopperReferenceEl] = useState<HTMLLIElement | null>(null);
  const classMenuItem = `flex relative items-center py-[6px] gap-2 px-3 select-none cursor-pointer ${item.additionalStyles} ${classes?.item}`;
  const textStyle = item.textStyleClasses || '';

  const getBorder = classes?.menu.match(/border-[^\s]+/);
  const separatorClass = getBorder ? `border-b ${getBorder}` : 'border-b border-gray-200';

  const subItemClass = ' context-menu__item';
  const baseIconStyles = 'w-4 h-4 text-neutral-500';
  const data: string =
    item.type === ContextMenuItemType.DEFAULT
      ? `context-menu-item-${item.title.toLowerCase().replace(' ', '-')}`
      : 'html';

  const isHovered = useHover(elementRef);

  const onMenuItemRef = (node: HTMLLIElement) => {
    elementRef.current = node;
    setPopperReferenceEl(node);
  };

  const onItemClick = () => {
    if (item.action && !item.disabled) {
      item.action();
    }

    if (!('closeOnClick' in item && item.closeOnClick === false)) {
      closeContextMenu();
    }
  };

  const menuItemDefaultTemplate = (item: MenuItemDefault) => (
    <>
      {item.icon && (
        <div className={`${baseIconStyles} ${item.iconStyles ? item.iconStyles : ''}`}>
          <Icon icon={item.icon}></Icon>
        </div>
      )}
      {item.title}
      {item.newFeature && <span className="px-1.5 ml-auto bg-teal-50 text-teal-600 select-none text-[10px]">NEW</span>}
      {item.isChecked && (
        <span className="ml-auto text-gray-500">
          <Icon icon={IconsMap.CHECK} className="w-3 h-3" />
        </span>
      )}
      {item.subMenu && (
        <span className="ml-auto text-gray-500">
          <Icon icon={IconsMap.ARROW_RIGHT} className="w-3 h-3" />
        </span>
      )}
    </>
  );

  const ContextMenuSubWrapper = ({ children }: { children: ReactElement }) => {
    if (usePopperSubmenu) {
      return (
        isHovered && (
          <ContextMenuPopperWrapper targetNode={popperReferenceEl} addClassNames="context-menu__sub max-h-screen z-20">
            {children}
          </ContextMenuPopperWrapper>
        )
      );
    } else {
      const submenuPosition = getContextSubMenuPosition(subX, subY);
      return <div className={classNames('context-menu__sub absolute hidden z-20', submenuPosition)}>{children}</div>;
    }
  };

  const renderMenuLiTemplate = (item: ContextMenuItemData) => (
    <li
      className={classNames(classMenuItem, {
        [separatorClass]: item.isSeparator,
        [textStyle]: !!item.textStyleClasses,
        [subItemClass]: !!item.subMenu,
        'opacity-30': !!item.disabled,
      })}
      onClick={onItemClick}
      data-cy={data}
      ref={usePopperSubmenu ? onMenuItemRef : undefined}
    >
      {item.type === ContextMenuItemType.HTML ? item.html : menuItemDefaultTemplate(item)}
      {item.subMenu && (
        <ContextMenuSubWrapper>
          <ContextMenuSub
            menuData={item.subMenu}
            closeContextMenu={closeContextMenu}
            subX={subX}
            subY={subY}
            theme={theme}
            classes={classes}
            additionStyles={item.subMenuAdditionStyles}
            usePopperSubmenu={usePopperSubmenu}
          />
        </ContextMenuSubWrapper>
      )}
    </li>
  );

  const renderMenuLi = (item: ContextMenuItemData) => {
    if (item.tooltip) {
      return (
        <TippyTooltip message={item.tooltip ? item.tooltip : ''} position={UiTooltipPosition.RIGHT} display="block">
          {renderMenuLiTemplate(item)}
        </TippyTooltip>
      );
    }

    return renderMenuLiTemplate(item);
  };

  return renderMenuLi(item);
};
